import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { FormManagementWorkspaceData } from '@/contracts/form-management'
import { BarChart3, CircleAlert, GitCompare, Lightbulb } from 'lucide-react'
import { useMemo, useState } from 'react'

type FormManagementCrossAnalysisProps = {
  workspaceData: FormManagementWorkspaceData
}

export function FormManagementCrossAnalysis({ workspaceData }: FormManagementCrossAnalysisProps) {
  const [xQuestionId, setXQuestionId] = useState(workspaceData.crossAnalysis.xQuestionId)
  const [yQuestionId, setYQuestionId] = useState(workspaceData.crossAnalysis.yQuestionId)
  const xQuestion = workspaceData.crossAnalysisQuestions.find((question) => question.id === xQuestionId)
  const yQuestion = workspaceData.crossAnalysisQuestions.find((question) => question.id === yQuestionId)
  const unsupportedQuestions = workspaceData.crossAnalysisQuestions.filter((question) => !question.supported)

  const cellMap = useMemo(
    () =>
      new Map(
        workspaceData.crossAnalysis.cells.map((cell) => [
          `${cell.row}:${cell.column}`,
          cell,
        ]),
      ),
    [workspaceData.crossAnalysis.cells],
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant="outline">cross analysis contract preview</Badge>
        <Badge variant={workspaceData.panelStates.crossAnalysis === 'ready' ? 'success' : 'warning'}>
          {workspaceData.panelStates.crossAnalysis}
        </Badge>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <GitCompare className="size-5 text-indigo-600" />
                <h3 className="font-semibold text-slate-950">交叉分析</h3>
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
                選擇兩個可聚合題目，檢視答案交叉分佈、樣本數與簡短洞察。此頁只使用
                synthetic data，不讀取 production response table。
              </p>
            </div>
            <div className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600">
              樣本數：{workspaceData.crossAnalysis.sampleSize}
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-slate-700">X 軸題目</label>
              <Select value={xQuestionId} onValueChange={setXQuestionId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="選擇 X 軸題目" />
                </SelectTrigger>
                <SelectContent>
                  {workspaceData.crossAnalysisQuestions.map((question) => (
                    <SelectItem
                      key={question.id}
                      value={question.id}
                      disabled={!question.supported}
                    >
                      {question.label}（{question.typeLabel}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Y 軸題目</label>
              <Select value={yQuestionId} onValueChange={setYQuestionId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="選擇 Y 軸題目" />
                </SelectTrigger>
                <SelectContent>
                  {workspaceData.crossAnalysisQuestions.map((question) => (
                    <SelectItem
                      key={question.id}
                      value={question.id}
                      disabled={!question.supported}
                    >
                      {question.label}（{question.typeLabel}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">X：{xQuestion?.label ?? '未選擇'}</Badge>
            <Badge variant="secondary">Y：{yQuestion?.label ?? '未選擇'}</Badge>
          </div>
        </CardContent>
      </Card>

      {unsupportedQuestions.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CircleAlert className="mt-0.5 size-5 text-amber-500" />
              <div>
                <div className="font-medium text-slate-950">部分題型尚不支援</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {unsupportedQuestions.map((question) => (
                    <Badge key={question.id} variant="warning" title={question.disabledReason}>
                      {question.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <BarChart3 className="size-5 text-violet-600" />
              <h3 className="font-semibold text-slate-950">交叉表 preview</h3>
            </div>
            <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-28">題目</TableHead>
                    {workspaceData.crossAnalysis.columns.map((column) => (
                      <TableHead key={column} className="min-w-28">
                        {column}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workspaceData.crossAnalysis.rows.map((row) => (
                    <TableRow key={row}>
                      <TableCell className="font-medium text-slate-900">{row}</TableCell>
                      {workspaceData.crossAnalysis.columns.map((column) => {
                        const cell = cellMap.get(`${row}:${column}`)
                        return (
                          <TableCell key={column}>
                            <div className="font-medium text-slate-900">{cell?.count ?? 0}</div>
                            <div className="text-xs text-slate-500">{cell?.percentage ?? 0}%</div>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2">
              <Lightbulb className="size-5 text-amber-500" />
              <h3 className="font-semibold text-slate-950">洞察摘要</h3>
            </div>
            <div className="mt-4 space-y-3">
              {workspaceData.crossAnalysis.insights.map((insight) => (
                <div key={insight} className="rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-600">
                  {insight}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
